import { expandBySelector, ensureActiveBySelector } from '../primitives/GuidedTour';

/**
 * 0DTE guided-tour step catalog.
 *
 * Same contract as the screener's catalog — all module-specific content and
 * prepare helpers live here, while the GuidedTour engine and useTourState hook
 * (components/primitives/) stay module-agnostic. Copy mirrors the preset
 * tooltips in utils/presetMappings.js.
 *
 * Anchors (data-tour attributes) referenced below:
 *   Form:    tickers-section, direction-section, expiration-section,
 *            strike-mode-toggle, underlying-chart, strike-section, form-actions
 *   Results: view-toggle, view-detail, view-heatmap, heatmap-settings,
 *            results-filters, heatmap-controls, heatmap-table, row-actions,
 *            simulation-settings, column-tabs
 */

// Demo selection populated by the tour (only into empty sections). One ticker —
// 0DTE analyses a single underlying — with an ITM and an OTM near-money strike
// across the two shortest bands, so the heatmap shows real contrast.
const DEMO = {
  tickers: ['SPY'],
  direction: 'buy_call',
  expiration: ['0-1w', '1-3w'],
  strike: ['itm-0-05', 'otm-0-05'],
};

// AccordionFormSection: root .accordion-section, header .section-header,
// expanded content .section-content.expanded. One-way expand (never collapses).
const expandAccordion = (sel) =>
  expandBySelector(sel, '.section-header', '.section-content.expanded');

// View switching: ScreenerResponsive toggle buttons carry .active when selected.
export const ensureDetailView = () =>
  ensureActiveBySelector('[data-tour="view-detail"]', 'active');
export const ensureHeatmapView = () =>
  ensureActiveBySelector('[data-tour="view-heatmap"]', 'active');

// HeatmapSettingsPanel root is .simulation-settings; its clickable header is
// the first child div; expanded content contains a range slider.
const expandHeatmapSettings = () =>
  expandBySelector('[data-tour="heatmap-settings"]', '.simulation-settings > div:first-child', 'input[type="range"]');

// Small settle delay so a just-clicked view toggle can mount its tree before
// the engine polls for the step's anchor.
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * @param {Object} formRef - React ref to the 0DTE form, exposing
 *   tourPopulate(part, value). Optional — steps degrade gracefully.
 */
export function getZeroDteTourSteps(formRef) {
  // Expand the step's accordion section, then fill it with the demo value if
  // the user hasn't picked anything there yet.
  const expandAndPopulate = (sel, part) => () => {
    expandAccordion(sel);
    if (formRef?.current?.tourPopulate && DEMO[part] != null) {
      formRef.current.tourPopulate(part, DEMO[part]);
    }
  };

  const formSteps = [
    {
      id: 'welcome',
      target: null,
      placement: 'center',
      title: 'Welcome to the 0DTE Screener',
      body: [
        'This module is built for short-dated contracts — same-day expiries out to about three months — where gamma and theta dominate and a 0.5% move in the underlying can swing a premium hard.',
        'You analyse one ticker at a time, so every scenario column can show the actual underlying price alongside the % move. This tour builds a real demo scan and runs it. Skip anytime — relaunch from the Guide button next to the page title.',
      ],
    },
    {
      id: 'ticker',
      target: '[data-tour="tickers-section"]',
      prepare: expandAndPopulate('[data-tour="tickers-section"]', 'tickers'),
      title: 'Pick one ticker',
      body: 'We\'ve added SPY as a demo. 0DTE analyses a single underlying at a time — picking another ticker replaces this one. That\'s what lets the results show one live price for every scenario.',
    },
    {
      id: 'direction',
      target: '[data-tour="direction-section"]',
      prepare: expandAndPopulate('[data-tour="direction-section"]', 'direction'),
      title: 'Choose a market direction',
      body: 'Bullish buys calls — you profit if the underlying rises. Bearish buys puts. You can also sell premium: cash-secured puts (neutral/bullish income) and selling calls — note the high-risk warning, naked calls have unlimited loss potential. We\'ve selected Bullish for the demo.',
    },
    {
      id: 'expiration',
      target: '[data-tour="expiration-section"]',
      prepare: expandAndPopulate('[data-tour="expiration-section"]', 'expiration'),
      title: 'Expiration: short-dated bands',
      body: 'These bands run from 0DTE out to about three months. The shorter the band, the more gamma and theta dominate — a 0.5% move can swing a 0–1 week premium hard, while 2–3 month contracts respond more to volatility and to any earnings inside the window. We\'ve selected the two shortest bands for the demo.',
    },
    {
      id: 'strike-mode',
      target: '[data-tour="strike-mode-toggle"]',
      title: 'Two ways to position strikes',
      body: [
        '% ITM/OTM picks strikes by their distance from the current price — the bands below run from deep in the money to far out of the money, fine-grained near the money where short-dated contracts are most sensitive.',
        'Target Price flips it around: you name the price you think the underlying is heading to, and the screener pulls the strikes sitting at that level — either the single nearest strike, or every strike inside a buffer you set around it.',
      ],
    },
    {
      id: 'underlying-chart',
      target: '[data-tour="underlying-chart"]',
      prepare: () => expandBySelector(
        '[data-tour="underlying-chart"]', '.collapsible-header', '.zerodte-chart-body'
      ),
      title: 'Pick your target off the chart',
      body: 'Your ticker\'s price history sits here and follows you as you scroll. Change the range or add indicators from its header. In Target Price mode a dashed line marks your target and a shaded band shows the strikes your buffer will pull in — and clicking any point on the price line sets that level as your target.',
    },
    {
      id: 'strike',
      target: '[data-tour="strike-section"]',
      prepare: expandAndPopulate('[data-tour="strike-section"]', 'strike'),
      title: 'Strike: probability vs. leverage',
      body: [
        'Negative % = in the money: higher delta and cost, higher probability. ATM (~50 delta) carries maximum gamma — the sweet spot for short-dated moves. Positive % = out of the money: cheaper, high leverage, needs a sharp move to pay off.',
        'We\'ve pre-selected one just-ITM and one just-OTM band — every expiration × strike combination becomes its own scenario, so the results heatmap shows both trade-offs side by side.',
      ],
    },
    {
      id: 'actions',
      target: '[data-tour="form-actions"]',
      nextLabel: 'Run scan',
      onNext: () => {
        document.querySelector('[data-tour="form-actions"] button:first-child')?.click();
      },
      title: 'Run it',
      body: [
        'Run executes your selections immediately. + Add to Queue stacks multiple setups to run in one batch. Save as preset keeps this configuration for next time (it appears in the panel on the right).',
        'Click Run scan below — we\'ll run this demo setup and the tour continues automatically in the results.',
      ],
    },
  ];

  // --- Concrete heatmap example (one contract, one scenario column) --------
  // Chosen fresh on every activation of the example step; the target selector
  // is a function re-evaluated per engine poll tick, so it also handles the
  // table mounting a beat after the view switch.
  let examplePct = null;
  const EXAMPLE_ROW = '[data-tour="heatmap-table"] tbody tr:first-child';

  const pickScenarioPct = () => {
    const ths = document.querySelectorAll('[data-tour="heatmap-table"] thead [data-tour-scenario]');
    const vals = Array.from(ths)
      .map((th) => parseFloat(th.getAttribute('data-tour-scenario')))
      .filter((v) => !isNaN(v));
    if (vals.length === 0) return null;
    // Prefer the positive move closest to +1% — 0DTE's default columns are
    // fine-grained (±0.5% … ±3%), so +1% is the representative one.
    const pool = vals.some((v) => v > 0) ? vals.filter((v) => v > 0) : vals;
    return pool.reduce((best, v) => (Math.abs(v - 1) < Math.abs(best - 1) ? v : best), pool[0]);
  };

  const readCellText = (sel) => {
    const el = document.querySelector(sel);
    if (!el) return null;
    const text = (el.textContent || '').replace('↗', '').trim();
    return text || null;
  };

  const resultsSteps = [
    {
      id: 'view-toggle',
      target: '[data-tour="view-toggle"]',
      prepare: ensureHeatmapView,
      title: 'Three views of the same results',
      body: 'Elasticity Heatmap (the default) shows projected returns across underlying-move scenarios. Detail View is the full data table. Stories is a card-based summary. The tour covers the heatmap first, then the Detail View.',
    },
    {
      id: 'heatmap-settings',
      target: '[data-tour="heatmap-settings"]',
      prepare: async () => {
        ensureHeatmapView();
        await wait(150);
        expandHeatmapSettings();
      },
      title: 'Time to exit, in hours if you need it',
      body: [
        'The slider sets how long you plan to hold before selling. Because 0DTE contracts can expire today, this one switches units — Days, Hours, or 30-minute steps — and the projection uses the real fractional time to the 4pm ET close, not a whole-day approximation.',
        'The scenario chips control which underlying-move columns appear, each labelled with both the % move and the price it implies. Two extras live here for short-dated contracts: the IV model (sticky-moneyness slides IV along the volatility smile as price moves — recommended) and earnings vol-crush, which appears when a scheduled earnings date falls inside a contract\'s window.',
      ],
    },
    {
      id: 'filters',
      target: '[data-tour="results-filters"]',
      title: 'Filter the results',
      body: 'Filter by expiration date, ticker, and strike position. Combine them freely — they only filter what\'s shown, they never re-run the scan.',
    },
    {
      id: 'display-controls',
      target: '[data-tour="heatmap-controls"]',
      title: 'Multiplier vs. Premium Gain — plus your toolkit',
      body: [
        'Premium Gain % shows the option\'s projected % return in each scenario. Underlying Multiplier shows leverage: option gain ÷ underlying move, so "5x" means the option gains ~5% for every 1% the underlying moves. Short-dated near-money contracts post the highest multipliers here.',
        'On the right: show Favorites only, Share the heatmap as an image, or Export CSV.',
      ],
    },
    {
      id: 'heatmap-example',
      prepare: () => {
        ensureHeatmapView();
        examplePct = null; // re-picked by the target function once the table is in the DOM
      },
      target: () => {
        if (examplePct == null) examplePct = pickScenarioPct();
        if (examplePct == null) return null; // no scenario columns → engine auto-skips
        return `${EXAMPLE_ROW} [data-tour-scenario="${examplePct}"]`;
      },
      extraTargets: () => [
        `[data-tour="heatmap-table"] thead [data-tour-scenario="${examplePct}"]`,
        `${EXAMPLE_ROW} [data-tour-col="days-to-exit"]`,
      ],
      title: 'Read one cell like a sentence',
      body: () => {
        const pct = examplePct;
        const ticker = readCellText(`${EXAMPLE_ROW} .table-cell-sticky-1`) || 'the first contract';
        const type = readCellText(`${EXAMPLE_ROW} .table-cell-sticky-3`) || '';
        const strike = readCellText(`${EXAMPLE_ROW} .table-cell-sticky-4`) || '';
        const days = readCellText(`${EXAMPLE_ROW} [data-tour-col="days-to-exit"]`);
        const cellText = pct != null
          ? readCellText(`${EXAMPLE_ROW} [data-tour-scenario="${pct}"]`)
          : null;

        const contract = [ticker, strike, type].filter(Boolean).join(' ');
        const dir = pct > 0 ? 'rising' : pct < 0 ? 'falling' : 'staying flat';
        const movePhrase = pct === 0 ? 'staying flat' : `${dir} ${Math.abs(pct)}%`;
        const isMultiplier = cellText != null && /x$/i.test(cellText);
        const outcome = !cellText || cellText === '--'
          ? 'shows the projected result of exactly that move'
          : isMultiplier
            ? `is projected to move ${cellText.replace(/x$/i, '×')} the underlying's move`
            : `is projected to change ${cellText}`;
        const daysPhrase = days ? `the highlighted Days-to-Exit value (${days})` : 'the highlighted Days-to-Exit value';

        return [
          'Each row is one contract; each colored column is an underlying-move scenario, headed by both the % move and the price it implies.',
          `Take the first row — the ${contract}. The highlighted column header is the move: ${ticker} ${movePhrase}. The highlighted cell is the payoff: if that move happens by your exit time (${daysPhrase}), Black-Scholes says this contract's premium ${outcome}.`,
          'Every cell reads the same way: the underlying reaches that price by your exit time → premium changes Y%. Hover any cell for the projected premium, underlying price, and breakeven; click a ticker for its chart.',
        ];
      },
    },
    {
      id: 'row-actions',
      target: '[data-tour="row-actions"]',
      title: 'Keep, save, or dismiss',
      body: 'The star pins a contract as a favorite — it floats to the top and enables the Favorites filter. The plus saves it to your Watchlist (you pick which groups it joins). The minus hides a row you\'ve ruled out.',
    },
    {
      id: 'detail-sim',
      target: '[data-tour="simulation-settings"]',
      prepare: async () => {
        ensureDetailView();
        await wait(250);
      },
      title: 'The Detail View: every number behind the heatmap',
      body: 'This is the full table. Its Simulation Settings add an Underlying Gain slider alongside time-to-exit, stepped in 0.5% increments for short-dated moves — drag either and every simulated column recomputes instantly in your browser. No new data is fetched, so experiment freely.',
    },
    {
      id: 'column-tabs',
      target: '[data-tour="column-tabs"]',
      title: '30+ columns, organized',
      body: 'Use the tabs to focus the table: Simulation (projected returns), Sell Metrics, Contract Details, Greeks, and Market Data — or All. Hover any column header for a plain-English explanation. The same filters and row actions from the heatmap work here too.',
    },
    {
      id: 'done',
      target: null,
      placement: 'center',
      title: "You're set",
      body: [
        "That's the 0DTE screener: one ticker, a short-dated expiration window, strikes by percentage or by target price — then stress-test the contracts in the heatmap and drill into the Detail View.",
        'Relaunch this tour anytime from the Guide button next to the page title.',
      ],
    },
  ];

  return { formSteps, resultsSteps };
}
